/**
 * Hook for risk classification nomenclature (structural risk — ORDIN 1761).
 * One doc per organization. Loads or seeds default on first access.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  riskClassificationNomenclatureService
} from '../services/riskClassificationNomenclatureService';

const EMPTY_LEVELS = [];

/**
 * @param {string} organizationId
 * @param {{ seedIfEmpty?: boolean, userId?: string }} options - seedIfEmpty: seed default when no nomenclature; userId required for seed
 * @returns {{ nomenclature: object | null, loading: boolean, error: string | null, save: function, seedDefault: function, refresh: function }}
 */
export function useRiskClassificationNomenclature(organizationId, options = {}) {
  const { seedIfEmpty = false, userId = null } = options;
  const [nomenclature, setNomenclature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setNomenclature(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await riskClassificationNomenclatureService.getNomenclature(organizationId);
      setNomenclature(data);
      if (seedIfEmpty && userId && (!data || !data.levels || data.levels.length === 0)) {
        await riskClassificationNomenclatureService.seedDefaultNomenclature(organizationId, userId);
        const after = await riskClassificationNomenclatureService.getNomenclature(organizationId);
        setNomenclature(after);
      }
    } catch (err) {
      console.error('[useRiskClassificationNomenclature] load:', err);
      setError(err.message || 'Failed to load nomenclature');
      setNomenclature(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, seedIfEmpty, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (levels, saveUserId) => {
    if (!organizationId || !saveUserId) throw new Error('organizationId and userId required');
    await riskClassificationNomenclatureService.saveNomenclature(organizationId, levels, saveUserId);
    const updated = await riskClassificationNomenclatureService.getNomenclature(organizationId);
    setNomenclature(updated);
  }, [organizationId]);

  const seedDefault = useCallback(async () => {
    if (!organizationId || !userId) throw new Error('organizationId and userId required');
    await riskClassificationNomenclatureService.seedDefaultNomenclature(organizationId, userId);
    const updated = await riskClassificationNomenclatureService.getNomenclature(organizationId);
    setNomenclature(updated);
  }, [organizationId, userId]);

  const refresh = useCallback(() => load(), [load]);

  return {
    nomenclature,
    levels: nomenclature?.levels ?? EMPTY_LEVELS,
    loading,
    error,
    save,
    seedDefault,
    refresh
  };
}
